/**
 * truncateMiddle — elides the MIDDLE of a string when it exceeds `max`
 * characters, keeping head + tail (`abc…xyz`) rather than the classic
 * tail-cut (`abc…`). Subquery snippets, long identifiers and window-function
 * bodies are often most informative at BOTH ends (leading clause keyword +
 * trailing alias/column), so middle-elision keeps a usable hint at each end
 * instead of hiding everything past a fixed prefix.
 *
 * Pure, engine-agnostic string utility — no SQL awareness at all.
 * Unicode-safe: iterates Unicode code points (via `Array.from`), not UTF-16
 * code units, so astral characters (emoji, surrogate pairs) are never split
 * into an unpaired half.
 *
 * Standing rule (Advisor 2 §B5): any label truncated with this util MUST
 * carry a full-text tooltip alongside it — the ellipsis hides information,
 * the tooltip is what recovers it on hover. See the adoption in
 * `SubquerySection.tsx`'s derived-table snippet.
 */
export function truncateMiddle(text: string, max: number): string {
  if (max <= 0) return '';
  const chars = Array.from(text);
  if (chars.length <= max) return text;
  if (max === 1) return '…';

  // Reserve one slot for the ellipsis glyph itself; split the remainder
  // between head and tail (head gets the extra char on an odd split, so the
  // leading clause/keyword — usually the more useful anchor — reads a touch
  // longer).
  const keep = max - 1;
  const headLen = Math.ceil(keep / 2);
  const tailLen = Math.floor(keep / 2);
  const head = chars.slice(0, headLen).join('');
  const tail = tailLen > 0 ? chars.slice(chars.length - tailLen).join('') : '';
  return `${head}…${tail}`;
}
